import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ShieldCheck, Loader2, LayoutDashboard, Users, FolderKanban, BadgeDollarSign,
  RefreshCw, Eye, Building2, PenTool, DollarSign, TrendingUp, Ban, Power, Trash2, EyeOff,
} from "lucide-react";
import { adminStats, adminUsers, adminUserDetail, adminCases, adminReconcileUsers, adminSuspendUser, adminDeleteUser, adminHideCase, adminDeleteCase } from "../services/payments";
import { fmtUSD } from "../lib/pricing";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ScopeChips, matchesScope, isDemoClass } from "../components/ScopeChips";
import AdminPayouts from "./AdminPayouts";

const TOKEN_KEY = "dcc_admin_token";
const fmtDate = (ms) => (ms ? new Date(ms).toLocaleDateString() : "—");

const MetricCard = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-xl border border-border bg-card p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
      <Icon className="h-4 w-4 text-brand" /> {label}
    </div>
    <div className="mt-2 font-heading text-2xl font-bold">{value}</div>
  </div>
);

function DashboardSection({ token }) {
  const { t } = useTranslation();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("REAL");
  useEffect(() => {
    setLoading(true);
    adminStats(token, scope.toLowerCase()).then(setS).catch(() => {}).finally(() => setLoading(false));
  }, [token, scope]);
  const m = t("admin.metrics", { returnObjects: true });
  return (
    <div>
      <div className="mb-4 flex w-fit justify-end ml-auto">
        <ScopeChips value={scope} onChange={setScope} testid="dashboard-scope" />
      </div>
      {loading || !s ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="admin-dashboard-metrics">
          <MetricCard icon={Users} label={m.usersTotal} value={s.usersTotal} testid="metric-users" />
          <MetricCard icon={PenTool} label={m.designers} value={s.designers} testid="metric-designers" />
          <MetricCard icon={Building2} label={m.labs} value={s.labs} testid="metric-labs" />
          <MetricCard icon={FolderKanban} label={m.casesTotal} value={s.casesTotal} testid="metric-cases" />
          <MetricCard icon={FolderKanban} label={m.casesActive} value={s.casesActive} testid="metric-active" />
          <MetricCard icon={FolderKanban} label={m.casesCompleted} value={s.casesCompleted} testid="metric-completed" />
          <MetricCard icon={DollarSign} label={m.volume} value={fmtUSD(s.volumeProcessed)} testid="metric-volume" />
          <MetricCard icon={TrendingUp} label={m.commissions} value={fmtUSD(s.commissionsGenerated)} testid="metric-commissions" />
        </div>
      )}
    </div>
  );
}

function UsersSection({ token }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [scope, setScope] = useState("REAL");
  const load = useCallback(() => {
    setLoading(true);
    adminUsers(token).then(setUsers).catch(() => {}).finally(() => setLoading(false));
  }, [token]);
  useEffect(() => { load(); }, [load]);
  const roleLabel = (u) => u.role === "designer" ? t("admin.roleDesigner") : (u.accountType || t("admin.roleLab"));
  const visibleUsers = users.filter((u) => matchesScope(isDemoClass(u), scope));

  const recover = async () => {
    setRecovering(true);
    try {
      const res = await adminReconcileUsers(token);
      if (res.recovered > 0) toast.success(t("admin.recoveredCount", { count: res.recovered }));
      else toast.success(t("admin.recoveredNone"));
      load();
    } catch { toast.error(t("admin.recoverFailed")); }
    finally { setRecovering(false); }
  };

  const open = async (uid) => {
    setDetailLoading(true); setDetail({});
    try { setDetail(await adminUserDetail(token, uid)); } catch { toast.error("Failed"); setDetail(null); }
    finally { setDetailLoading(false); }
  };

  const [acting, setActing] = useState("");
  const doAction = async (fn, confirmKey) => {
    if (confirmKey && !window.confirm(t(confirmKey))) return;
    setActing("x");
    try { await fn(); toast.success(t("admin.actionDone")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("admin.actionFailed")); }
    finally { setActing(""); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;
  return (
    <div data-testid="admin-users-section">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-lg font-semibold">{t("admin.usersTitle")}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <ScopeChips value={scope} onChange={setScope} options={["REAL", "DEMO", "ALL"]} testid="users-scope" />
          <Button size="sm" variant="outline" className="rounded-full" onClick={recover} disabled={recovering} data-testid="reconcile-users-button">
            {recovering ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />}
            {t("admin.recoverAccounts")}
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2">{t("admin.name")}</th><th className="px-3 py-2">{t("admin.email")}</th>
              <th className="px-3 py-2">{t("admin.role")}</th><th className="px-3 py-2">{t("admin.country")}</th>
              <th className="px-3 py-2">{t("admin.registered")}</th><th className="px-3 py-2">{t("admin.profileStatus")}</th>
              <th className="px-3 py-2">{t("admin.accountStatus")}</th><th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">{t("admin.noUsers")}</td></tr>}
            {users.length > 0 && visibleUsers.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">{t("admin.noneForFilter")}</td></tr>}
            {visibleUsers.map((u) => (
              <tr key={u.uid} className="border-t border-border" data-testid={`user-row-${u.uid}`}>
                <td className="px-3 py-2 font-medium">
                  <div className="flex items-center gap-1.5">
                    {u.name}
                    {u.isDemo && <Badge className="bg-slate-400 text-white hover:bg-slate-400" data-testid={`user-tag-${u.uid}`}>{t("admin.demo")}</Badge>}
                  </div>
                </td>
                <td className="px-3 py-2">{u.email}</td>
                <td className="px-3 py-2"><Badge variant="secondary">{roleLabel(u)}</Badge></td>
                <td className="px-3 py-2">{u.country || "—"}</td>
                <td className="px-3 py-2">{fmtDate(u.createdAt)}</td>
                <td className="px-3 py-2">
                  <Badge className={u.profileComplete ? "bg-emerald-600 text-white hover:bg-emerald-600" : "bg-amber-500 text-white hover:bg-amber-500"}>
                    {u.profileComplete ? t("admin.complete") : t("admin.incomplete")}
                  </Badge>
                </td>
                <td className="px-3 py-2">
                  <Badge className={u.status === "suspended" ? "bg-red-600 text-white hover:bg-red-600" : ""} variant={u.status === "suspended" ? undefined : "secondary"} data-testid={u.status === "suspended" ? `suspended-${u.uid}` : `active-${u.uid}`}>
                    {u.status === "suspended" ? t("admin.suspended") : t("admin.active")}
                  </Badge>
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap justify-end gap-1.5">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => open(u.uid)} data-testid={`view-profile-${u.uid}`}>
                      <Eye className="mr-1 h-3.5 w-3.5" /> {t("admin.viewProfile")}
                    </Button>
                    {u.status === "suspended" ? (
                      <Button size="sm" variant="outline" className="rounded-full text-emerald-700" disabled={!!acting} onClick={() => doAction(() => adminSuspendUser(token, u.uid, false), "admin.confirmReactivate")} data-testid={`reactivate-user-${u.uid}`}>
                        <Power className="mr-1 h-3.5 w-3.5" /> {t("admin.reactivate")}
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="rounded-full text-amber-700" disabled={!!acting} onClick={() => doAction(() => adminSuspendUser(token, u.uid, true), "admin.confirmSuspend")} data-testid={`suspend-user-${u.uid}`}>
                        <Ban className="mr-1 h-3.5 w-3.5" /> {t("admin.suspend")}
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="rounded-full text-red-600" disabled={!!acting} onClick={() => doAction(() => adminDeleteUser(token, u.uid), "admin.confirmDeleteUser")} data-testid={`delete-user-${u.uid}`}>
                      <Trash2 className="mr-1 h-3.5 w-3.5" /> {t("admin.deleteUser")}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg bg-white" data-testid="user-detail-modal">
          <DialogHeader><DialogTitle>{t("admin.profileInfo")}</DialogTitle></DialogHeader>
          {detailLoading ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div> : detail?.user && (
            <div className="max-h-[65vh] space-y-3 overflow-y-auto text-sm">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                <div><span className="text-muted-foreground">{t("admin.name")}: </span>{detail.user.name}</div>
                <div><span className="text-muted-foreground">{t("admin.email")}: </span>{detail.user.email}</div>
                <div><span className="text-muted-foreground">{t("admin.role")}: </span>{detail.user.role === "designer" ? t("admin.roleDesigner") : (detail.user.accountType || t("admin.roleLab"))}</div>
                <div><span className="text-muted-foreground">{t("admin.phone")}: </span>{detail.user.phone || "—"}</div>
                <div><span className="text-muted-foreground">{t("admin.country")}: </span>{detail.user.country || "—"}</div>
                <div><span className="text-muted-foreground">{t("admin.registered")}: </span>{fmtDate(detail.user.createdAt)}</div>
              </div>
              {(detail.user.address?.street || detail.user.address?.city) && (
                <p className="text-muted-foreground">{[detail.user.address.street, detail.user.address.city, detail.user.address.state, detail.user.address.postalCode, detail.user.address.country].filter(Boolean).join(", ")}</p>
              )}
              <div className="border-t border-border pt-2">
                <p className="mb-1 font-semibold">{t("admin.ownedCases")} ({(detail.ownedCases || []).length})</p>
                {(detail.ownedCases || []).map((c) => (
                  <div key={c.id} className="flex items-center justify-between py-0.5 text-xs">
                    <span>{c.title} {c.isDemo && <Badge className="ml-1 bg-slate-400 text-white">{t("admin.demo")}</Badge>}</span>
                    <span className="text-muted-foreground">{c.status}</span>
                  </div>
                ))}
                {(detail.assignedCases || []).length > 0 && <p className="mb-1 mt-2 font-semibold">{t("admin.assignedCases")} ({detail.assignedCases.length})</p>}
                {(detail.assignedCases || []).map((c) => (
                  <div key={c.id} className="flex items-center justify-between py-0.5 text-xs">
                    <span>{c.title}</span><span className="text-muted-foreground">{c.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CasesSection({ token }) {
  const { t } = useTranslation();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState("");
  const [scope, setScope] = useState("REAL");
  const load = useCallback(() => {
    setLoading(true);
    adminCases(token).then(setCases).catch(() => {}).finally(() => setLoading(false));
  }, [token]);
  useEffect(() => { load(); }, [load]);
  const visibleCases = cases.filter((c) => matchesScope(isDemoClass(c), scope));
  const doAction = async (fn, confirmKey) => {
    if (confirmKey && !window.confirm(t(confirmKey))) return;
    setActing("x");
    try { await fn(); toast.success(t("admin.actionDone")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("admin.actionFailed")); }
    finally { setActing(""); }
  };
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;
  return (
    <div data-testid="admin-cases-section">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-lg font-semibold">{t("admin.casesTitle")}</h2>
        <ScopeChips value={scope} onChange={setScope} options={["REAL", "DEMO", "ALL"]} testid="cases-scope" />
      </div>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2">{t("admin.name")}</th><th className="px-3 py-2">{t("admin.lab")}</th>
              <th className="px-3 py-2">{t("admin.designer")}</th><th className="px-3 py-2">{t("admin.status")}</th>
              <th className="px-3 py-2">{t("admin.date")}</th><th className="px-3 py-2 text-right">{t("admin.price")}</th>
              <th className="px-3 py-2 text-right">{t("admin.commission")}</th><th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {cases.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">{t("admin.noCases")}</td></tr>}
            {cases.length > 0 && visibleCases.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">{t("admin.noneForFilter")}</td></tr>}
            {visibleCases.map((c) => (
              <tr key={c.id} className="border-t border-border" data-testid={`case-row-${c.id}`}>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap items-center gap-1.5 font-medium">
                    {c.title}
                    <Badge className={c.isDemo ? "bg-slate-400 text-white hover:bg-slate-400" : "bg-emerald-600 text-white hover:bg-emerald-600"} data-testid={`case-tag-${c.id}`}>
                      {c.isDemo ? t("admin.demo") : t("admin.real")}
                    </Badge>
                    {c.hidden && <Badge className="bg-amber-500 text-white hover:bg-amber-500" data-testid={`case-hidden-${c.id}`}>{t("admin.hidden")}</Badge>}
                  </div>
                </td>
                <td className="px-3 py-2">{c.lab}</td>
                <td className="px-3 py-2">{c.designer || "—"}</td>
                <td className="px-3 py-2"><Badge variant="secondary">{c.status}</Badge></td>
                <td className="px-3 py-2">{fmtDate(c.createdAt)}</td>
                <td className="px-3 py-2 text-right">{c.amountPaid != null ? fmtUSD(c.amountPaid) : (c.budget != null ? fmtUSD(c.budget) : "—")}</td>
                <td className="px-3 py-2 text-right">{c.commission != null ? fmtUSD(c.commission) : "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap justify-end gap-1.5">
                    {c.hidden ? (
                      <Button size="sm" variant="outline" className="rounded-full text-emerald-700" disabled={!!acting} onClick={() => doAction(() => adminHideCase(token, c.id, false))} data-testid={`unhide-case-${c.id}`}>
                        <Eye className="mr-1 h-3.5 w-3.5" /> {t("admin.unhide")}
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="rounded-full text-amber-700" disabled={!!acting} onClick={() => doAction(() => adminHideCase(token, c.id, true))} data-testid={`hide-case-${c.id}`}>
                        <EyeOff className="mr-1 h-3.5 w-3.5" /> {t("admin.hide")}
                      </Button>
                    )}
                    {c.canDelete && (
                      <Button size="sm" variant="outline" className="rounded-full text-red-600" disabled={!!acting} onClick={() => doAction(() => adminDeleteCase(token, c.id), "admin.confirmDeleteCase")} data-testid={`delete-case-${c.id}`}>
                        <Trash2 className="mr-1 h-3.5 w-3.5" /> {t("admin.deleteCaseAction")}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("dashboard");

  const authenticate = useCallback(async (tk) => {
    setLoading(true);
    try {
      await adminStats(tk); // validates the token
      localStorage.setItem(TOKEN_KEY, tk);
      setAuthed(true);
    } catch {
      setAuthed(false);
      toast.error(t("admin.invalidToken"));
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { if (token) authenticate(token); /* eslint-disable-next-line */ }, []);

  if (!authed) {
    return (
      <div className="mx-auto max-w-md px-4 py-20" data-testid="admin-login">
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold"><ShieldCheck className="h-6 w-6" /> {t("admin.dashboardTitle")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("admin.enterToken")}</p>
        <Input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Admin token" className="mt-4" data-testid="admin-token-input" />
        <Button className="mt-3 w-full rounded-full bg-brand text-white hover:bg-brand/90" onClick={() => authenticate(token)} disabled={!token || loading} data-testid="admin-login-button">
          {loading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} {t("admin.enter")}
        </Button>
      </div>
    );
  }

  const nav = [
    { id: "dashboard", label: t("admin.nav.dashboard"), icon: LayoutDashboard },
    { id: "users", label: t("admin.nav.users"), icon: Users },
    { id: "cases", label: t("admin.nav.cases"), icon: FolderKanban },
    { id: "payouts", label: t("admin.nav.payouts"), icon: BadgeDollarSign },
  ];

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8" data-testid="admin-dashboard">
      <h1 className="mb-5 flex items-center gap-2 font-heading text-2xl font-bold"><ShieldCheck className="h-6 w-6 text-brand" /> {t("admin.dashboardTitle")}</h1>
      <div className="mb-6 flex flex-wrap gap-2 border-b border-border">
        {nav.map((n) => (
          <button key={n.id} onClick={() => setTab(n.id)} data-testid={`admin-tab-${n.id}`}
            className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${tab === n.id ? "border-brand text-brand" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <n.icon className="h-4 w-4" /> {n.label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && <DashboardSection token={token} />}
      {tab === "users" && <UsersSection token={token} />}
      {tab === "cases" && <CasesSection token={token} />}
      {tab === "payouts" && <div className="-mx-4 sm:-mx-6 lg:-mx-8"><AdminPayouts /></div>}
    </div>
  );
}
